import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  DeleteOutlineOutlined,
  SendRounded,
  SupportAgentOutlined,
} from "@mui/icons-material";
import { Chip, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { API_ORIGIN as API_URL } from "../../config/env";
import { AdminPageLayout } from "../../components/admin/AdminPageLayout";

const statuses = ["all", "open", "in_progress", "resolved", "closed"];
const TICKET_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 20;
const TERMINAL_STATUSES = ["resolved", "closed"];

function authHeaders() {
  return { rf_token_header123_k: localStorage.getItem("rfis") };
}

function timeAgo(value) {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(Math.floor(diff / 60000), 0);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function messageTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function statusColor(status) {
  if (status === "resolved" || status === "closed") return "success";
  if (status === "in_progress") return "warning";
  return "info";
}

function isClosedForReplies(status) {
  return TERMINAL_STATUSES.includes(status);
}

function mergeMessagesById(existing, incoming, mode = "replace") {
  if (mode === "replace") return incoming;
  const byId = new Map();
  const ordered = mode === "prepend" ? [...incoming, ...existing] : [...existing, ...incoming];
  ordered.forEach((message) => {
    if (message?.id != null) byId.set(message.id, message);
  });
  return Array.from(byId.values()).sort((a, b) => {
    const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (timeDiff !== 0) return timeDiff;
    return Number(a.id) - Number(b.id);
  });
}

export default function SupportTicketsPage() {
  const [searchParams] = useSearchParams();
  const deepLinkTicketId = Number.parseInt(searchParams.get("ticketId"), 10);
  const deepLinkAppliedRef = useRef(false);
  const messageListRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedId = selectedTicket?.id;
  const canReply = selectedTicket && !isClosedForReplies(selectedTicket.status);

  const fetchTickets = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: TICKET_PAGE_SIZE };
      if (status !== "all") params.status = status;
      if (search.trim()) params.search = search.trim();

      const res = await axios.get(`${API_URL}/api/v1/admin/support/tickets`, {
        headers: authHeaders(),
        params,
      });
      const data = res.data?.data || {};
      const nextTickets = data.tickets || [];
      setTickets(nextTickets);
      setStats(data.stats || {});
      setTicketPage(Number(data.page) || page);
      setTicketTotalPages(Math.max(Number(data.totalPages) || 1, 1));

      setSelectedTicket((current) => {
        if (!deepLinkAppliedRef.current && Number.isFinite(deepLinkTicketId)) {
          const deepLinked = nextTickets.find((ticket) => ticket.id === deepLinkTicketId);
          if (deepLinked) {
            deepLinkAppliedRef.current = true;
            return deepLinked;
          }
        }
        if (current && nextTickets.some((ticket) => ticket.id === current.id)) {
          return nextTickets.find((ticket) => ticket.id === current.id) || current;
        }
        return nextTickets[0] || null;
      });
    } catch (error) {
      toast.error("Failed to load support tickets");
    } finally {
      setLoading(false);
    }
  }, [status, search, deepLinkTicketId]);

  const fetchMessages = useCallback(async (ticketId, { beforeId, preserveScroll } = {}) => {
    if (!ticketId) {
      setMessages([]);
      setHasMoreOlder(false);
      return;
    }
    try {
      const params = { limit: MESSAGE_PAGE_SIZE };
      if (beforeId) params.beforeId = beforeId;

      const listEl = messageListRef.current;
      const previousHeight = listEl?.scrollHeight || 0;

      const res = await axios.get(`${API_URL}/api/v1/admin/support/tickets/${ticketId}/messages`, {
        headers: authHeaders(),
        params,
      });
      const data = res.data?.data || {};
      const nextBatch = data.messages || [];

      setSelectedTicket((current) => data.ticket || current);
      setHasMoreOlder(Boolean(data.hasMoreOlder));
      setMessages((current) =>
        mergeMessagesById(current, nextBatch, beforeId ? "prepend" : "replace")
      );

      if (preserveScroll && listEl) {
        requestAnimationFrame(() => {
          const nextHeight = listEl.scrollHeight || 0;
          listEl.scrollTop = nextHeight - previousHeight;
        });
      } else if (stickToBottomRef.current && listEl) {
        requestAnimationFrame(() => {
          listEl.scrollTop = listEl.scrollHeight;
        });
      }
    } catch (error) {
      toast.error("Failed to load ticket messages");
    }
  }, []);

  useEffect(() => {
    setTicketPage(1);
  }, [status, search]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchTickets(ticketPage);
    }, search ? 300 : 0);
    return () => clearTimeout(delay);
  }, [fetchTickets, ticketPage, search]);

  useEffect(() => {
    stickToBottomRef.current = true;
    setMessages([]);
    setHasMoreOlder(false);
    fetchMessages(selectedId);
  }, [selectedId, fetchMessages]);

  const kpis = useMemo(
    () => [
      ["Total", Object.values(stats).reduce((sum, value) => sum + Number(value || 0), 0)],
      ["Open", stats.open || 0],
      ["In Progress", stats.in_progress || 0],
      ["Closed", (stats.closed || 0) + (stats.resolved || 0)],
    ],
    [stats]
  );

  const loadOlderMessages = async () => {
    if (!selectedId || !hasMoreOlder || loadingOlder || !messages.length) return;
    setLoadingOlder(true);
    stickToBottomRef.current = false;
    try {
      await fetchMessages(selectedId, {
        beforeId: messages[0].id,
        preserveScroll: true,
      });
    } finally {
      setLoadingOlder(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || !canReply || !reply.trim()) return;
    try {
      const formData = new FormData();
      formData.append("message", reply.trim());
      await axios.post(`${API_URL}/api/v1/admin/support/tickets/${selectedId}/messages`, formData, {
        headers: authHeaders(),
      });
      setReply("");
      stickToBottomRef.current = true;
      await fetchMessages(selectedId);
      await fetchTickets(ticketPage);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to send reply";
      toast.error(message);
    }
  };

  const updateTicket = async (updates) => {
    if (!selectedId) return;
    try {
      await axios.put(`${API_URL}/api/v1/admin/support/tickets/${selectedId}`, updates, {
        headers: authHeaders(),
      });
      stickToBottomRef.current = true;
      await fetchMessages(selectedId);
      await fetchTickets(ticketPage);
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const deleteTicket = async () => {
    if (!selectedId || !window.confirm("Delete this support ticket?")) return;
    try {
      await axios.delete(`${API_URL}/api/v1/admin/support/tickets/${selectedId}`, {
        headers: authHeaders(),
      });
      setSelectedTicket(null);
      setMessages([]);
      await fetchTickets(ticketPage);
    } catch (error) {
      toast.error("Failed to delete ticket");
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Ticket Support"
      title="Support Inbox"
      description="Review user support tickets and reply from a ticket-based chat workspace."
    >
      <section className="support-kpi-grid">
        {kpis.map(([label, value]) => (
          <div className="support-kpi" key={label}>
            <span>{label}</span>
            <strong>{Number(value || 0).toLocaleString("en-IN")}</strong>
          </div>
        ))}
      </section>

      <section className="support-board">
        <aside className="support-list-panel">
          <div className="support-list-toolbar">
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search tickets"
              size="small"
              fullWidth
            />
            <div className="support-filter-row">
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                fullWidth
              >
                {statuses.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>

          <div className="support-ticket-list">
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                className={`support-ticket ${selectedId === ticket.id ? "support-ticket--active" : ""}`}
                onClick={() => setSelectedTicket(ticket)}
              >
                <span className="support-ticket__avatar">{ticket.user?.name?.[0] || "U"}</span>
                <span className="support-ticket__main">
                  <strong>{ticket.title}</strong>
                  <small>
                    {ticket.user?.name || "User"} · {ticket.user?.mobile || "-"}
                  </small>
                  <em>{ticket.lastMessage || "No messages yet"}</em>
                </span>
                <span className="support-ticket__meta">
                  <Chip size="small" label={ticket.status.replace("_", " ")} color={statusColor(ticket.status)} />
                  <small>{timeAgo(ticket.lastMessageAt)}</small>
                  {ticket.unreadCount ? <b>{ticket.unreadCount}</b> : null}
                </span>
              </button>
            ))}
            {!tickets.length ? (
              <div className="admin-empty-state">{loading ? "Loading tickets..." : "No support tickets found."}</div>
            ) : null}
          </div>

          {ticketTotalPages > 1 ? (
            <div className="support-list-pagination">
              <button
                type="button"
                disabled={ticketPage <= 1 || loading}
                onClick={() => setTicketPage((page) => Math.max(page - 1, 1))}
              >
                Previous
              </button>
              <span>
                Page {ticketPage} of {ticketTotalPages}
              </span>
              <button
                type="button"
                disabled={ticketPage >= ticketTotalPages || loading}
                onClick={() => setTicketPage((page) => Math.min(page + 1, ticketTotalPages))}
              >
                Next
              </button>
            </div>
          ) : null}
        </aside>

        <main className="support-chat-panel">
          {selectedTicket ? (
            <>
              <header className="support-chat-header">
                <div>
                  <strong>{selectedTicket.title}</strong>
                  <span>
                    {selectedTicket.user?.name || "User"} · {selectedTicket.user?.mobile || "-"}
                  </span>
                </div>
                <div className="support-chat-actions">
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={selectedTicket.status}
                    onChange={(event) => updateTicket({ status: event.target.value })}
                  >
                    {statuses
                      .filter((item) => item !== "all")
                      .map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.replace("_", " ")}
                        </MenuItem>
                      ))}
                  </TextField>
                  <button type="button" className="support-icon-button" onClick={deleteTicket} aria-label="Delete ticket">
                    <DeleteOutlineOutlined fontSize="small" />
                  </button>
                </div>
              </header>

              <div className="support-message-list" ref={messageListRef}>
                {hasMoreOlder ? (
                  <div className="support-load-older">
                    <button type="button" disabled={loadingOlder} onClick={loadOlderMessages}>
                      {loadingOlder ? "Loading..." : "Load earlier messages"}
                    </button>
                  </div>
                ) : null}
                {messages.map((message) => (
                  <div key={message.id} className={`support-message support-message--${message.senderType}`}>
                    <span className="support-message__avatar">
                      {message.senderType === "admin" ? <SupportAgentOutlined fontSize="small" /> : "U"}
                    </span>
                    <div>
                      <p>{message.message}</p>
                      {message.imageUrl ? (
                        <a href={`${API_URL}/${message.imageUrl}`} target="_blank" rel="noreferrer">
                          View attachment
                        </a>
                      ) : null}
                      <small>{messageTime(message.createdAt)}</small>
                    </div>
                  </div>
                ))}
              </div>

              {canReply ? (
                <footer className="support-composer">
                  <div className="support-composer__field">
                    <textarea
                      value={reply}
                      rows={4}
                      onChange={(event) => setReply(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          event.preventDefault();
                          sendReply();
                        }
                      }}
                      placeholder="Write your reply..."
                    />
                  </div>
                  <button
                    type="button"
                    className="support-send-button support-composer__send"
                    onClick={sendReply}
                    aria-label="Send reply"
                  >
                    <SendRounded />
                  </button>
                </footer>
              ) : (
                <div className="support-composer-closed">
                  This ticket is {selectedTicket.status.replace("_", " ")}. Replies are disabled.
                </div>
              )}
            </>
          ) : (
            <div className="support-chat-empty">Select a ticket to view the conversation.</div>
          )}
        </main>
      </section>
    </AdminPageLayout>
  );
}
